import {IDropdownMenuItem} from '@atomaro/ui-kit/components/DropdownMenu/types';
import {TEvent} from 'entities/calendar';
import {ReactNode} from 'react';

export type PersonName = {
  first: string;
  last: string;
  patronymic: string;
};

export type Credentials = {
  login: string;
  email: string;
};

export type Address = {
  city: string;
};

export type Owner = {
  id: string;
  realm?: TUserRealm;
  credentials: TUserCredentials;
  name: TUserName;
  address: TUserAddress;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  profile: TUserProfile;
};

export type Coordinates = {
  latitude: string;
  longitude: string;
};

export type ApiaryType = {
  id: string;
  name: string;
  number: number;
  coordinates: Coordinates;
  owner: Owner;
  participants?: Owner[];
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
};

export interface IDictionary {
  id: string;
  name: string;
  apiary: Partial<ApiaryType>;
  description?: string;
  owner: Owner;
  createdAt: string;
  updatedAt: string;
  type: string;
}

export type Hive = {
  id: string;
  apiary: ApiaryType;
  number: number;
  isSmart: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type PaginationResponse = {
  index: number;
  size: number;
};

export type SortResponse = {
  field: string;
  direction: string;
};

export type FtsResponse = {
  pattern: string;
  type: string;
};

export enum SensorTypeEnum {
  IN_TEMPERATURE = 'IN_TEMP',
  IN_HUMIDITY = 'IN_HUM',
  OUT_TEMPERATURE = 'OUT_TEMPERATURE',
  WEIGHT = 'WEIGHT',
  ACC_X = 'ACC_X',
  ACC_Y = 'ACC_Y',
  ACC_Z = 'ACC_Z',
  GYRO_X = 'GYRO_X',
  GYRO_Y = 'GYRO_Y',
  GYRO_Z = 'GYRO_Z',
  BEE_C_OUT = 'BEE_C_OUT',
  BEE_C_IN = 'BEE_C_IN',
  CO2 = 'CO2',
  ROOF_OPEN = 'ROOF_OPEN',
  ILLUMINATION = 'ILLUMINATION',
  TIME = 'TIME',
  POWER_CONS = 'POWER_CONS',
}

export type DrawerProps = {
  isOpen: boolean;
  position: 'top' | 'bottom' | 'left' | 'right' | 'submenu';
  children?: ReactNode;
  showOverlay?: boolean;
  onClose: () => void;
  borderRadius?: number[];
};

export type TUserCredentials = {
  email: string;
  login: string;
};

export type TUserRealm = {
  name: string;
  [key: string]: any;
};

export type TUserName = {
  first: string;
  last: string;
  patronymic: string;
  [key: string]: any;
};

export type TUserAddress = {
  city: string;
  [key: string]: any;
};

export type TUserProfile = {
  birthday: string;
  avatar?: {
    createdAt: string;
    id: string;
    path: string;
  };
};

export type TUserInfo = {
  id: string;
  realm?: TUserRealm;
  credentials: TUserCredentials;
  name: TUserName;
  address: TUserAddress;
  profile: TUserProfile;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  timezone: string | null;
};

export type oldNewValue = {
  old: number | string;
  new: number | string;
};

export type Attachment = {
  createdAt: string;
  id: string;
  path: string;
};

export type DiaryEntry = {
  id: string;
  hive: Hive;
  author: Owner;
  frames: number;
  strength: number;
  brood: number;
  note: string;
  dictionaries: IDictionary[];
  inspectedAt: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  attachments: Attachment[];
  framesPrev?: number;
  strengthPrev?: number;
  broodPrev?: number;
  events: TEvent[];
  fields: CustomFieldsTypeResponse[];
};

export type NewEntrieFormValues = {
  inspectedAt: Date;
  hives: IDropdownMenuItem[];
  frames: number;
  strength: number;
  brood: number;
  note: string;
  fields: CustomFieldsType[];
  attachmentIds: string[];
};

export type NewEventFormValues = {
  hiveIds: string[];
  hives: IDropdownMenuItem[];
  description: string;
  startAt: Date;
  endAt: Date;
  startAtUtc: string;
  endAtUtc: string;
  notifyAt?: Date;
  notifyAtNum?: number | string;
  notifyAtRepeat?: Date;
  notifyAtRepeatNum?: number | string;
  diaryEntryId?: string;
};

export type CreateEventType = {
  hiveIds: string[];
  hives: IDropdownMenuItem[];
  description: string;
  startAt: string;
  endAt: string;
  notifyAt?: Date;
  notifyAtNum?: number | string;
  notifyAtRepeat?: Date;
  notifyAtRepeatNum?: number | string;
  diaryEntryId?: string;
};

export type CustomFieldsTypeResponse = {
  id: string;
  name: string;
  type: 'TEXT' | 'NUMBER' | 'DATE' | 'DICTIONARY' | string;
  value?: string | number;
  dictionaries?: {
    id: string;
    name: string;
  }[];
};

export type CustomFieldsType = {
  dictionaries?: {id: string; name: string; type?: string}[];
  id: string;
  name: string;
  type: 'TEXT' | 'NUMBER' | 'DATE' | 'DICTIONARY' | string;
  value?: string | number;
  dictionaryIds?: string[];
};

export type DiaryFiltersFormValues = {
  authorIds: string[];
  inspectedAtFromDate: string | null;
  inspectedAtToDate: string | null;
  inspectedAtFrom: string | null;
  inspectedAtTo: string | null;
  framesFrom: string;
  framesTo: string;
  broodFrom: string;
  broodTo: string;
  strengthFrom: string;
  strengthTo: string;
  dictionariesIds: string[];
  hasAttachments: boolean | null;
  commentKeyWords: string[];
  hiveIds: string[];
  sortBy: string;
  sort: 'ASC' | 'DESC';
};

declare global {
  interface Window {
    ym?: (counterId: number, action: string, ...args: unknown[]) => void;
  }
}
